import React, { useContext, useMemo, useState } from 'react';

import { useStatsigClient } from '@statsig/react-bindings';
import { Trans, useTranslation } from 'react-i18next';
import Carousel from 'react-material-ui-carousel';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as HappyBuddy } from '../../../../assets/icons/buddy-happy-open-eyes.svg';
import { ReactComponent as CurrencyBuddy } from '../../../../assets/icons/v3/buddy-currency-2.svg';
import BdsStepsIndicator from '../../../../shared/components/v3/BdsStepsIndicator/BdsStepsIndicator';
import BdsButton from '../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import CustomModal from '../../../../shared/components/v3/Modals/CustomModal/CustomModal';
import { COLLABORATORS_ADD_DEPENDENTS_ROUTE } from '../../../../shared/constants/auth';
import { CAROUSEL_INTERVAL } from '../../../../shared/constants/home';
import { ARROW_RIGHT_ICON } from '../../../../shared/constants/images';
import { PLAN_TYPE_HEALTH } from '../../../../shared/constants/plan';
import { CAN_SEE_MANAGE_COLLABORATORS_DEPENDENTS_OPTION_FG } from '../../../../shared/constants/statsig';
import {
  ADD_DEPENDENT_MODAL_CHOICE,
  CLICK_ACTION,
  MANAGE_COLLABORATORS_SELECTION
} from '../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import { IAddCollaboratorsData } from '../../../../shared/interfaces/collaborators';
import TagManagerService from '../../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_COLLABORATORS } from '../../../../shared/translations/collaborators';
import { GenericVoidFunction } from '../../../../shared/types/genericTypes';
import { formatDateForDisplay, isCurrentDayFromDayOfMonth } from '../../../../shared/utils/datetime/datetime';
import { isDateAfterToday, redirectToPage } from '../../../../shared/utils/utils';
import './SubscribeSuccessModal.scss';

interface FirstSlideDescriptionProps {
  isPending: boolean;
  email: string;
  startDate: string;
  count: number;
}

const FirstSlideDescription = ({ isPending, email, startDate, count }: FirstSlideDescriptionProps) => {
  const { isUSA } = useContext(CompanyContext);
  const descriptionKey = isPending
    ? TRANSLATION_COLLABORATORS.modal.addCollaborators.pendingDialogDescription
    : TRANSLATION_COLLABORATORS.modal.addCollaborators.dialogDescription;

  const formattedDate = formatDateForDisplay(startDate, isUSA, '-');

  return (
    <Trans
      i18nKey={descriptionKey}
      values={{
        user_email: email,
        dd_mm_aaaa: formattedDate
      }}
      count={count}
      components={{ b: <b /> }}
    />
  );
};

interface ISubscribeSuccessModalProps {
  open: boolean;
  addCollabsData: IAddCollaboratorsData;
  onClose: GenericVoidFunction;
}

const SubscribeSuccessModal = ({ open, addCollabsData, onClose }: ISubscribeSuccessModalProps) => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const { client } = useStatsigClient();

  const { isMexico } = useContext(CompanyContext);

  const canAddDependentsFG = useMemo(
    () => client?.checkGate(CAN_SEE_MANAGE_COLLABORATORS_DEPENDENTS_OPTION_FG),
    [client]
  );

  const showDependents = useMemo(() => {
    if (isMexico && isCurrentDayFromDayOfMonth(25)) {
      return false;
    }
    return addCollabsData.supportDependents;
  }, [isMexico, addCollabsData]);

  const isPendingModal = (): boolean => {
    if (!addCollabsData.startDate) return false;
    return isDateAfterToday(addCollabsData.startDate) && addCollabsData.type === PLAN_TYPE_HEALTH;
  };

  const slidesConfig = [
    {
      Icon: HappyBuddy,
      title: () =>
        t(
          isPendingModal()
            ? TRANSLATION_COLLABORATORS.modal.addCollaborators.pendingDialogTitle
            : TRANSLATION_COLLABORATORS.modal.addCollaborators.dialogTitle,
          { count: addCollabsData.emails.length }
        ),
      description: () => (
        <FirstSlideDescription
          isPending={isPendingModal()}
          email={addCollabsData.emails[0]}
          startDate={addCollabsData.startDate}
          count={addCollabsData.emails.length}
        />
      ),
      buttonText: showDependents
        ? t(TRANSLATION_COLLABORATORS.modal.addCollaborators.buttonNext)
        : t(TRANSLATION_COLLABORATORS.modal.addCollaborators.buttonEnd),
      buttonIcon: showDependents ? ARROW_RIGHT_ICON : undefined
    }
  ];

  if (showDependents && canAddDependentsFG) {
    slidesConfig.push({
      Icon: CurrencyBuddy,
      title: () => t(TRANSLATION_COLLABORATORS.modal.addCollaborators.dependents.title),
      description: () => <>{t(TRANSLATION_COLLABORATORS.modal.addCollaborators.dependents.description)}</>,
      buttonText: t(TRANSLATION_COLLABORATORS.modal.addCollaborators.dependents.button, {
        count: addCollabsData.emails.length
      }),
      buttonIcon: undefined
    });
  }

  const currentSlideConfig = slidesConfig[currentSlide];
  const totalSlides = slidesConfig.length;

  const handleNext = () => {
    if (slidesConfig.length <= 1) return onClose();
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      TagManagerService.pushEvent(CLICK_ACTION, MANAGE_COLLABORATORS_SELECTION, ADD_DEPENDENT_MODAL_CHOICE);
      redirectToPage(navigate, COLLABORATORS_ADD_DEPENDENTS_ROUTE);
    }
  };

  return (
    <CustomModal open={open} onClose={onClose}>
      <Carousel
        className="ssm-carousel"
        stopAutoPlayOnHover={true}
        interval={CAROUSEL_INTERVAL}
        navButtonsAlwaysInvisible={true}
        index={currentSlide}
        indicators={false}
        autoPlay={false}
        swipe={false}
      >
        <div className="ssm-container" data-experience="new">
          <currentSlideConfig.Icon width={150} height={150} />
          <div className="flex flex-col gap-8">
            <div className="ssm-title">{currentSlideConfig.title()}</div>
            <div className="ssm-description">{currentSlideConfig.description()}</div>
          </div>
          <div className="flex flex-row justify-between items-center">
            {totalSlides > 1 && (
              <BdsStepsIndicator
                steps={totalSlides}
                active={currentSlide}
                variant="circle"
                changeStep={setCurrentSlide}
              />
            )}
            <BdsButton variant="primary" size="l" action={handleNext} iconRight={currentSlideConfig.buttonIcon}>
              {currentSlideConfig.buttonText}
            </BdsButton>
          </div>
        </div>
      </Carousel>
    </CustomModal>
  );
};

export default SubscribeSuccessModal;
